import { HttpClient, HttpParams } from '@angular/common/http';
import { effect, Injectable, signal } from '@angular/core';
import { Observable, catchError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { GScoreDistribution } from '../../../shared/models/charts/gscore-distribution';
import { CompanyV2 } from '../../../shared/models/company/company-v2';
import { Card } from '../../pages/overview/overview.component';
import { Router, ActivatedRoute } from '@angular/router';
import { NotificationsService } from '../../../shared/services/notifications.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { OverviewService } from '../../pages/overview/services/Overview.service';
import { StatusGraphHistoryRequest } from '../matching/models/status-graph-history/StatusGraphHistoryRequest';
import { StatusGraphHistoryResponse } from '../matching/models/status-graph-history/StatusGraphHistoryResponse';
import { CandidateStatus } from '../../pages/candidates/features/candidate-grid-manager/atoms/candidate-status-pill/candidate-status-pill.component';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  private readonly apiUrl: string = environment.apiUrl;
  public companiesSignal = signal<CompanyV2[]>([]);
  companies$ = toObservable(this.companiesSignal);

  public activeCompanySignal = signal<CompanyV2 | null>(null);
  activeCompany$ = toObservable(this.activeCompanySignal);

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationsService,
    private overviewService: OverviewService,
  ) {}

  public loadCompaniesData(): Observable<CompanyV2[]> {
    const url = this.apiUrl + 'api/v2/Navigation/companies';
    return this.httpClient.get<CompanyV2[]>(url).pipe(
      catchError((error) => {
        this.notificationService.showError(error);
        return [];
      }),
    );
  }

  public getActiveCompany(): Observable<CompanyV2 | null> {
    return this.activeCompany$;
  }

  public getCompanyCardData(
    startDate: Date,
    endDate: Date,
    companyId: number,
  ): Observable<Card[]> {
    const url = this.apiUrl + `api/Customer/OverviewStatusCardRange`;

    const params = new HttpParams()
      .set('cid', companyId)
      .set('startDate', startDate.toISOString().split('T')[0])
      .set('endDate', endDate.toISOString().split('T')[0]);

    return this.httpClient.get<Card[]>(url, { params: params });
  }

  public getGScoreDistributionData(
    startDate: Date,
    endDate: Date,
    company: CompanyV2,
  ): Observable<GScoreDistribution[]> {
    const url = this.apiUrl + `api/v2/Dashboard/GetCompanyGScores`;

    const params = new HttpParams()
      .set('cid', company.CompanyId.toString())
      .set('startDate', startDate.toISOString().split('T')[0])
      .set('endDate', endDate.toISOString().split('T')[0]);

    return this.httpClient.get<GScoreDistribution[]>(url, { params: params });
  }

  public getExtraLabelTexts(
    companyId: number
  ): Observable<string[]> {
    const url = this.apiUrl + `api/v2/Dashboard/GetExtraLabelTexts`;
    const params = new HttpParams()
      .set('companyId', companyId.toString())
    return this.httpClient.get<string[]>(url, { params : params});
  }

  public getCompanyCloseReasonsDistributionData(
    startDate: Date,
    endDate: Date,
    company: CompanyV2,
  ): Observable<CloseReasonStats[]> {
    const url = this.apiUrl + `api/v2/Dashboard/GetCloseReasonStats`;

    const params = new HttpParams()
      .set('companyId', company.CompanyId.toString())
      .set('startDate', startDate.toISOString().split('T')[0])
      .set('endDate', endDate.toISOString().split('T')[0]);

    return this.httpClient.get<CloseReasonStats[]>(url, { params: params });
  }

  public getStatusHistory(
    startDate: Date,
    endDate: Date,
    companyId: number,
    statusToBeFilteredOn?: CandidateStatus,
  ): Observable<StatusGraphHistoryResponse> {
    const url = this.apiUrl + `api/Customer/GetStatusHistory`;

    const request: StatusGraphHistoryRequest = {
      Status: statusToBeFilteredOn ?? 5,
      CompanyId: companyId,
      StartDate: startDate.toISOString().split('T')[0],
      EndDate: endDate.toISOString().split('T')[0],
    };

    return this.httpClient.post<StatusGraphHistoryResponse>(url, request);
  }

  public setActiveCompany(activeCompany: CompanyV2, updateUrl: boolean = true) {
    this.activeCompanySignal.update(() => activeCompany);

    if (updateUrl) {
      const shouldNavigateToCustomers =
        activeCompany.CompanyTypeV2 === 'Customer';

      const shouldNavigateToLegacy =
        this.router.url.includes('customers') &&
        activeCompany.CompanyTypeV2 === null;

      if (shouldNavigateToLegacy) {
        void this.router.navigate(['dashboard/overview/'], {
          // relativeTo: this.route,
          queryParams: { activeProject: activeCompany.CompanyId },
          queryParamsHandling: 'merge',
        });

        return;
      }

      if (shouldNavigateToCustomers) {
        void this.router.navigate(['dashboard/customers/'], {
          // relativeTo: this.route,
          queryParams: { activeProject: activeCompany.CompanyId },
          queryParamsHandling: 'merge',
        });

        return;
      } else {
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { activeProject: activeCompany.CompanyId },
          queryParamsHandling: 'merge',
        });
      }
    }

    this.overviewService.setStartDate(new Date(activeCompany.FirstCandidate));
  }

  public setActiveCompanyById(activeCompanyId: number) {
    const activeCompany = this.companiesSignal().find(
      (company) => company.CompanyId === activeCompanyId,
    );

    if (activeCompany) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { activeProject: activeCompanyId },
        queryParamsHandling: 'merge',
      });

      this.overviewService.setStartDate(new Date(activeCompany.FirstCandidate));

      this.activeCompanySignal.update(() => activeCompany);
    }
  }

  public updateQueryParamsWithActiveCompany(): void {
    const activeCompany = this.activeCompanySignal();
    if (activeCompany) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { activeProject: activeCompany.CompanyId },
        queryParamsHandling: 'merge',
      });
    }
  }
}

export interface CloseReasonStats {
  CloseReason: string;
  Count: number;
  Color: string;
  ValueCount: number;
  ValuePercentage: number;
}
